import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["urlInput", "status", "saveButton", "toggle"]
  static values = {
    clientId: String,
    platform: String,
    event: String,
    saveUrl: String,
    currentUrl: String
  }

  connect() {
    console.log(`Event Slack URL controller connected for ${this.platformValue}:${this.eventValue}`)
    this.updateVisibility()
  }

  // Called when the Slack toggle is changed
  toggleChanged() {
    this.updateVisibility()
  }

  updateVisibility() {
    const isEnabled = this.toggleTarget.checked
    const urlContainer = this.urlInputTarget.closest('.slack-url-container')
    
    if (isEnabled) {
      urlContainer.classList.remove('hidden')
      // Focus on the input if it's empty
      if (!this.urlInputTarget.value.trim()) {
        setTimeout(() => this.urlInputTarget.focus(), 100)
      }
    } else {
      urlContainer.classList.add('hidden')
    }
  }

  async save() {
    const url = this.urlInputTarget.value.trim()

    // Basic URL validation
    if (url && !this.isValidWebhookUrl(url)) {
      this.showStatus("Please enter a valid Slack webhook URL (should start with https://hooks.slack.com/)", "error")
      return
    }

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          platform: this.platformValue,
          event: this.eventValue,
          slack_webhook_url: url
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.showStatus(data.message || "Event-specific Slack URL saved successfully", "success")
        this.currentUrlValue = url
        this.updateUrlDisplay()
      } else {
        const error = await response.json()
        this.showStatus(error.error || "Failed to save event-specific Slack URL", "error")
      }
    } catch (error) {
      console.error("Error saving event-specific Slack URL:", error)
      this.showStatus("Failed to save event-specific Slack URL", "error")
    }
  }

  async test() {
    const url = this.urlInputTarget.value.trim() || this.currentUrlValue

    if (!url) {
      this.showStatus("Please enter a Slack webhook URL first", "error")
      return
    }

    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Testing..."

    try {
      const response = await fetch("/admin/client_notification_settings/test_slack_webhook", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          webhook_url: url,
          platform: this.platformValue,
          event: this.eventValue
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.showStatus(data.message || "Test message sent successfully!", "success")
      } else {
        const error = await response.json()
        this.showStatus(error.error || "Failed to send test message", "error")
      }
    } catch (error) {
      console.error("Error testing Slack webhook:", error)
      this.showStatus("Failed to send test message", "error")
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.textContent = "Save"
    }
  }

  showStatus(message, type) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.className = `text-xs mt-1 ${type === "success" ? "text-theme-success" : "text-theme-error"}`
      this.statusTarget.classList.remove("hidden")

      setTimeout(() => {
        this.statusTarget.classList.add("hidden")
      }, 5000)
    }
  }

  isValidWebhookUrl(url) {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === "https:" && urlObj.hostname === "hooks.slack.com"
    } catch {
      return false
    }
  }

  updateUrlDisplay() {
    // Update any display elements that show the current URL status
    const displayElement = document.querySelector(`[data-event-slack-status="${this.platformValue}-${this.eventValue}"]`)
    if (displayElement) {
      if (this.currentUrlValue) {
        displayElement.innerHTML = `<span class="text-theme-success">✓ Event-specific URL configured</span>`
      } else {
        displayElement.innerHTML = `<span class="text-theme-muted">Using global Slack URL (if configured)</span>`
      }
    }
  }
}
